import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { MessageSquare, Send } from 'lucide-react';
import messagingService from '../../services/MessagingService';

const PatientRow = ({ patient, clinicianName }) => {
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setSending(true);
    setSendStatus(null);

    try {
      // Patient ID from patients table is their user_id for login
      const patientUserId = patient.id;

      console.log('Sending message to patient:', {
        patientId: patientUserId,
        patientName: `${patient.firstname || patient.firstName} ${patient.lastinitial || patient.lastInitial}`,
        message: message.substring(0, 50) + '...'
      });

      await messagingService.sendMendyMessage(patientUserId, message);

      console.log('✅ Message sent successfully');
      setSendStatus('success');
      setMessage('');

      // Close dialog after short delay
      setTimeout(() => {
        setShowMessageDialog(false);
        setSendStatus(null);
      }, 1500);
    } catch (error) {
      console.error('❌ Error sending message:', error);
      console.error('Error details:', {
        patientId: patient.id,
        errorMessage: error.message,
        errorStack: error.stack
      });
      setSendStatus('error');
    } finally {
      setSending(false);
    }
  };

  const quickMessages = [
    "Great job on your progress this week! Keep up the excellent work.",
    "I noticed you haven't completed your exercises today. Remember, consistency is key!",
    "Your form is improving! Continue focusing on the exercises your clinician assigned.",
    "How are you feeling today? Remember to let your clinician know if you're experiencing any discomfort."
  ];

  const handleQuickMessage = (quickMsg) => {
    setMessage(quickMsg);
  };

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-gray-900">
            {patient.firstname || patient.firstName || 'Unknown'}{' '}
            {patient.lastinitial || patient.lastInitial || ''}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-600">
            {patient.roomnumber || patient.roomNumber ? (
              <>
                Room {patient.roomnumber || patient.roomNumber}
                {patient.bednumber || patient.bedNumber ? `, Bed ${patient.bednumber || patient.bedNumber}` : ''}
              </>
            ) : (
              'Not assigned'
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
              patient.status === 'active'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {patient.status || 'unknown'}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-600">
            {patient.assignedexercises || patient.assignedExercises || 0} assigned
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-600">
            {formatRelativeTime(patient.lastactive || patient.lastActive)}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <Button
            onClick={() => setShowMessageDialog(true)}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <MessageSquare className="h-4 w-4" />
            Message
          </Button>
        </td>
      </tr>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Send Message to {patient.firstname || patient.firstName} {patient.lastinitial || patient.lastInitial}
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-1">
              Under {clinicianName} • This message will appear in their "Chat with Mendy" as coming from Mendy
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Quick Messages */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Quick Messages:
              </label>
              <div className="grid grid-cols-1 gap-2">
                {quickMessages.map((quickMsg, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickMessage(quickMsg)}
                    className="text-left p-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                  >
                    {quickMsg}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Message */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Custom Message:
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
                className="w-full min-h-[120px] p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={sending}
              />
            </div>

            {/* Status Messages */}
            {sendStatus === 'success' && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                Message sent successfully! The patient will see it in their chat.
              </div>
            )}
            {sendStatus === 'error' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                Failed to send message. Please try again.
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                onClick={() => setShowMessageDialog(false)}
                variant="outline"
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={!message.trim() || sending}
                className="bg-[#6d4f79] hover:bg-[#5a3f66] text-white"
              >
                {sending ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatientRow;
